// SPA client-side OTel + Langfuse correlation (fn-1.31).
//
// **What this module is**:
//   1. A tiny browser-side helper that emits a Langfuse-shaped "user
//      action" span when the user clicks an interactive control
//      (approve / reject / start-run). The span carries the platform
//      `session_id` correlator so the server-side spans (BFF /
//      control-plane / MCP) join onto the same Langfuse trace.
//   2. A `traceparent` codec — same algorithm as
//      `@sb-ai-platform/observability/otel.ts`, but rewritten for the
//      browser (the observability package uses `node:crypto`; the
//      browser uses `crypto.subtle.digest` which is async).
//
// **Why a bespoke browser module, not the observability package directly**:
//   - The observability package imports `node:crypto` for trace-id
//     derivation. Bundling that into the SPA pulls a Node polyfill or
//     errors at build time.
//   - The SPA's "span emission" surface is fire-and-forget HTTP — there
//     is no SDK to swap in/out. The Langfuse browser ingest path is a
//     POST to a per-env ingestion endpoint with a JSON body.
//   - Keeping the SPA module self-contained means the SPA bundle is
//     bytes-conservative (the observability package's seam interfaces
//     would force a code-split).
//
// **Trace-id derivation**:
//   - The platform-side helper (`deterministicTraceIdFromSession`)
//     hashes the session id with SHA-256 and takes the first 32 hex
//     chars. The browser uses Web Crypto API
//     (`crypto.subtle.digest('SHA-256', ...)`) for the same algorithm.
//   - Web Crypto is async — the SPA's `init()` pre-computes the
//     trace-id once per session id and caches it. Subsequent
//     `trackUserAction(...)` calls read from the cache and emit
//     synchronously.
//
// **Failure isolation**: every public function never throws on
// observability failure. Browser environments without Web Crypto or
// without a configured ingest endpoint silently no-op. The SPA's UX
// MUST NOT depend on observability success.

/**
 * W3C `traceparent` header name. Matches the platform-side constant
 * (`packages/observability/src/otel.ts:TRACEPARENT_HEADER`).
 */
export const TRACEPARENT_HEADER = 'traceparent' as const;

/** W3C version byte — currently `00`. */
export const TRACEPARENT_VERSION = '00' as const;

/** Length of the trace-id field of `traceparent`, in hex chars. */
export const TRACE_ID_HEX_LENGTH = 32;

/** Length of the parent-id field of `traceparent`, in hex chars. */
export const PARENT_ID_HEX_LENGTH = 16;

/** Sampled flag — matches the platform's "always export" stance. */
export const FLAGS_SAMPLED = '01' as const;

/**
 * Public surface: a Langfuse span input as the SPA emits it. Matches
 * the platform-side `LangfuseSpanInput` shape on the fields the
 * Langfuse ingest API consumes; the SDK adapter on the platform side
 * accepts the same fields.
 *
 * **Why we don't reuse the platform-side type**: importing the
 * observability package would drag `node:crypto`. The SPA emits via a
 * fetch POST anyway — the wire shape, not the seam type, is what
 * matters.
 */
export interface ClientSpan {
  /** Span name — `spa.<action>` (e.g. `spa.approval.approve`). */
  readonly name: string;
  /**
   * 16-hex span id (W3C parent-id width). Same id is used as the
   * `parent-id` of any outbound `traceparent` the SPA mints in
   * response to this action — see `wrapFetch` / `buildTraceparent`.
   */
  readonly spanId: string;
  /** Lowercase 32-hex trace id (derived from session id). */
  readonly traceId: string;
  /**
   * Session correlator on the SPA span. Equal to `userSessionId` for
   * non-session-coupled actions, or the platform `sessions.id` for
   * session-coupled ones (set via `trackUserAction({ sessionId })`).
   * The SPA's outbound `traceparent` is anchored on this id's
   * derived trace-id.
   */
  readonly sessionId: string;
  /**
   * User-level session correlator (typically the Cognito user-id) set
   * at `initClientOtel({ sessionId })` time. **codex round 10**:
   * the BFF SPA-spans ingress route REQUIRES `sessionId ===
   * authenticatedUserId`; the beacon sink wires THIS field into the
   * POST body's `sessionId` and demotes the per-action override into
   * the body's `runSessionId` field. Internal `sessionId` (above)
   * stays platform-session-aware so cross-tier traceparent stitch
   * still works.
   */
  readonly userSessionId: string;
  /** Wall-clock window. Point-in-time clicks set both to the same instant. */
  readonly startTime: Date;
  readonly endTime: Date;
  /** Per-event metadata; structured + serializable. */
  readonly metadata: Readonly<Record<string, unknown>>;
  /** Optional Langfuse level — defaults to DEFAULT. */
  readonly level?: 'DEBUG' | 'DEFAULT' | 'WARNING' | 'ERROR';
}

/**
 * Sink shape: where the SPA sends spans. The default sink POSTs JSON
 * to the configured ingestion URL via `fetch`; tests pass a recording
 * sink that captures calls without going to the network. Errors are
 * swallowed — instrumentation is observe-only.
 */
export interface ClientSpanSink {
  emit(span: ClientSpan): void;
}

/** Module-level handle assembled by {@link initClientOtel}. */
export interface ClientOtelHandle {
  /**
   * Track a user action — emits a span synchronously via the sink.
   * Returns the minted `spanId` (16-hex W3C parent-id width) which:
   *   1. Is set on the emitted Langfuse observation, AND
   *   2. Becomes the `mostRecentSpanId` consulted by {@link wrapFetch}
   *      so any HTTP call made in response to the action carries
   *      `traceparent` whose parent-id is THIS span's spanId — the
   *      BFF then nests its own span under this id, closing the
   *      cross-tier chain.
   *
   * Returns `null` when trace id derivation failed.
   */
  trackUserAction(action: {
    readonly name: string;
    readonly metadata?: Readonly<Record<string, unknown>>;
    readonly level?: ClientSpan['level'];
    /**
     * Optional platform-session correlator (fn-1.31 round 12).
     *
     * **What this DOES NOT do (codex round 12)**: it does not change
     * the trace-id of the emitted SPA span, and it does not change
     * the outbound `traceparent`'s trace-id. The SPA always emits
     * + propagates on the user trace (`hash(userId)`) because the
     * BFF `/spa-spans` route refuses to attribute SPA spans to a
     * platform session UUID without server-side ownership
     * verification — emitting the SPA span on the user trace while
     * the BFF/CP/MCP tiers used the platform-session trace would
     * orphan the BFF span's parent-id reference.
     *
     * **What this DOES do**: it stamps the platform-session id into
     * the SPA span's `sessionId` field (and through to the wire body
     * as `runSessionId`) so a Langfuse reader can filter spans by
     * `metadata.run_session_id` to find a session's clicks.
     *
     * When omitted, the SPA span carries the handle's user-level
     * `sessionId` and no `runSessionId` is sent.
     */
    readonly sessionId?: string;
  }): string | null;
  /**
   * Build a `traceparent` header value for an outbound `fetch` from
   * the SPA. Returns `null` if the trace id couldn't be derived.
   * When a `trackUserAction` span has been emitted in the current
   * frame, that span's id is used as the `parent-id`; otherwise a
   * fresh random parent-id is minted.
   */
  buildTraceparent(): string | null;
  /**
   * Wrap `fetch` so every outbound call carries `traceparent`. The
   * wrapper is idempotent: pre-existing traceparent in headers is
   * preserved (W3C propagation rule).
   *
   * **Header preservation**: when `input` is a `Request`, its
   * existing headers are seeded as the base + overlaid with
   * `init?.headers`. Without this, custom headers (Authorization,
   * Content-Type) on a Request-wrapped fetch silently get dropped.
   */
  wrapFetch(fetchFn: typeof fetch): typeof fetch;
  /** The deterministic trace id this handle was initialized with. `null` when init failed. */
  readonly traceId: string | null;
  /** Smokeball session id this handle is bound to. */
  readonly sessionId: string;
  /** The most recently minted span id from `trackUserAction`. `null` until first call. */
  readonly mostRecentSpanId: () => string | null;
  /**
   * Pre-compute and cache the deterministic trace-id for a platform
   * `sessions.id` (fn-1.31 round 6). Call once when a session-coupled
   * view mounts (e.g. `/runs/:id`) so subsequent synchronous
   * `trackUserAction({ sessionId })` calls land on the right
   * Langfuse trace without an async hash on the click path.
   *
   * **Idempotent**. Returns the computed trace-id (or `null` if Web
   * Crypto wasn't available). Callers usually don't need the return
   * value — they fire-and-forget at mount time.
   */
  readonly primeSessionTrace: (sessionId: string) => Promise<string | null>;
}

/**
 * Inputs to {@link initClientOtel}.
 */
export interface InitClientOtelInputs {
  /** Smokeball platform session id (the platform-side `sessions.id`). */
  readonly sessionId: string;
  /** Sink — defaults to {@link createBeaconSpanSink} when omitted. */
  readonly sink?: ClientSpanSink;
  /**
   * Optional `crypto` provider for tests. Defaults to
   * `globalThis.crypto`. Tests can pass a fake whose `subtle.digest`
   * resolves synchronously to a known hash.
   */
  readonly crypto?: Pick<Crypto, 'subtle' | 'getRandomValues'>;
  /** Clock override for tests. Defaults to `() => new Date()`. */
  readonly now?: () => Date;
}

/**
 * Init the SPA OTel handle for a given session. Asynchronously
 * computes the deterministic trace id then resolves to the handle.
 *
 * **When to call**: once per session, after the SPA has the platform
 * session id (typically after `runs.start` returns or when navigating
 * into `/runs/:id`). Earlier calls fall back to "no-op" emission until
 * the trace id is ready.
 *
 * **Failure isolation**: a failed trace-id derivation (Web Crypto
 * unavailable) returns a handle whose `traceId === null`; subsequent
 * `trackUserAction` calls emit a span WITHOUT a trace-id (the
 * Langfuse ingest accepts this as a "best-effort" span). The SPA's
 * UX is unaffected.
 */
export async function initClientOtel(inputs: InitClientOtelInputs): Promise<ClientOtelHandle> {
  const crypto = inputs.crypto ?? globalThis.crypto;
  const sink = inputs.sink ?? createBeaconSpanSink();
  const now = inputs.now ?? ((): Date => new Date());
  const traceId = await computeTraceIdFromSession(inputs.sessionId, crypto);
  let mostRecentSpanId: string | null = null;
  let mostRecentTraceId: string | null = null;
  // **codex round 13 — single-trace-tree via session-override**:
  // session-coupled actions emit + propagate on
  // `hash(platformSessionId)` so SPA / BFF / control-plane / HITL /
  // MCP all land on ONE Langfuse trace per session. The BFF
  // `/spa-spans` route gates this by verifying control-plane
  // ownership of `runSessionId` before accepting the platform-
  // session attribution; if verification fails, the BFF emits the
  // SPA span on the user trace (no orphan parent: BFF's own span
  // also lands on the user trace via inbound traceparent).
  //
  // The primed trace-id cache lets click handlers fire
  // synchronously without an async hash on the click path.
  const primedTraceIds = new Map<string, string>();
  // Consumption semantics (codex round 2): both spanId AND traceId
  // are cleared once consumed so an unrelated polling fetch doesn't
  // inherit a stale click's parent-id / trace-id.
  const consumeRecent = (): { spanId: string | null; traceId: string | null } => {
    const next = { spanId: mostRecentSpanId, traceId: mostRecentTraceId };
    mostRecentSpanId = null;
    mostRecentTraceId = null;
    return next;
  };
  return {
    sessionId: inputs.sessionId,
    traceId,
    mostRecentSpanId: () => mostRecentSpanId,
    trackUserAction(action): string | null {
      // **codex round 13 — session-override on the SPA emit + outbound
      // `traceparent`.** Restored from round-6, BUT only safe to use
      // when the BFF can verify `runSessionId` ownership (round 13
      // ownership verifier on the `/spa-spans` route). Mismatch
      // between the SPA's chosen trace and the BFF's choice was the
      // orphan-parent bug in round 11; round 12 reverted by
      // single-tracking on user trace at the cost of split traces
      // for session-coupled actions; round 13 fixes both by binding
      // the SPA's trace choice to the BFF's verification outcome.
      //
      // When the caller passes `sessionId` AND `primeSessionTrace`
      // pre-warmed the override trace-id, the emitted SPA span and
      // the next outbound `traceparent` BOTH use
      // `hash(action.sessionId)`. Unprimed override degrades to the
      // user trace.
      //
      // **codex round 14 — split-trace bug on unprimed override**:
      // If `action.sessionId` is set but the prime hasn't resolved,
      // we must NOT advertise the platform-session as `runSessionId`
      // on the wire body. Doing so would (a) cause the beacon sink
      // to emit `runSessionId`, (b) trigger BFF ownership-verifier
      // to emit the SPA span on the platform-session trace, while
      // (c) the outbound `traceparent` we just minted is on the
      // user trace — splitting the click span and its tRPC child
      // across two traces. We tie the wire `sessionId` exposure to
      // the trace choice: only set `effectiveSessionId` to the
      // override when the override trace-id is in use. Unprimed
      // override silently degrades to a pure user-trace span (no
      // runSessionId, no BFF verifier hit).
      const overrideTraceId =
        action.sessionId !== undefined ? primedTraceIds.get(action.sessionId) : undefined;
      const effectiveTraceId = overrideTraceId ?? traceId;
      if (effectiveTraceId === null) return null;
      const effectiveSessionId =
        overrideTraceId !== undefined && action.sessionId !== undefined
          ? action.sessionId
          : inputs.sessionId;
      const spanId = mintParentSpanId(crypto);
      mostRecentSpanId = spanId;
      mostRecentTraceId = effectiveTraceId;
      const t = now();
      const span: ClientSpan = {
        name: action.name,
        spanId,
        traceId: effectiveTraceId,
        sessionId: effectiveSessionId,
        userSessionId: inputs.sessionId,
        startTime: t,
        endTime: t,
        metadata: action.metadata ?? {},
        ...(action.level !== undefined ? { level: action.level } : {}),
      };
      try {
        sink.emit(span);
      } catch {
        // observe-only — never propagate
      }
      return spanId;
    },
    async primeSessionTrace(sessionId: string): Promise<string | null> {
      // Pre-derive + cache `hash(sessionId)` so the next synchronous
      // `trackUserAction({ sessionId })` call lands on the override
      // trace. Idempotent.
      const cached = primedTraceIds.get(sessionId);
      if (cached !== undefined) return cached;
      const computed = await computeTraceIdFromSession(sessionId, crypto);
      if (computed !== null) primedTraceIds.set(sessionId, computed);
      return computed;
    },
    buildTraceparent(): string | null {
      const consumed = consumeRecent();
      const effectiveTraceId = consumed.traceId ?? traceId;
      if (effectiveTraceId === null) return null;
      const parentId = consumed.spanId ?? mintParentSpanId(crypto);
      return `${TRACEPARENT_VERSION}-${effectiveTraceId}-${parentId}-${FLAGS_SAMPLED}`;
    },
    wrapFetch(fetchFn): typeof fetch {
      return ((input: RequestInfo | URL, init?: RequestInit) => {
        const headers = new Headers();
        if (typeof Request !== 'undefined' && input instanceof Request) {
          for (const [k, v] of input.headers.entries()) headers.set(k, v);
        }
        if (init?.headers !== undefined) {
          const overlay = new Headers(init.headers);
          for (const [k, v] of overlay.entries()) headers.set(k, v);
        }
        if (!headers.has(TRACEPARENT_HEADER)) {
          const consumed = consumeRecent();
          const effectiveTraceId = consumed.traceId ?? traceId;
          if (effectiveTraceId === null) {
            return fetchFn(input, init);
          }
          const parentId = consumed.spanId ?? mintParentSpanId(crypto);
          headers.set(
            TRACEPARENT_HEADER,
            `${TRACEPARENT_VERSION}-${effectiveTraceId}-${parentId}-${FLAGS_SAMPLED}`,
          );
        }
        return fetchFn(input, { ...init, headers });
      }) as typeof fetch;
    },
  };
}

/**
 * Module-level "active handle" registry — the SPA's `ClientOtelProvider`
 * sets this when init resolves so non-React surfaces (e.g. the
 * module-level tRPC `httpLink` fetch) can call `wrapFetch` without
 * threading the handle through React props.
 *
 * **Why a module global, not a React-only hook**: the tRPC client is
 * constructed at module-init time (`trpc-client.ts`), BEFORE any
 * React tree mounts. Its `httpLink.fetch` callback runs in the
 * client's call site, not in a React component — so it can't
 * `useContext`. A module-level pointer set by the provider's useEffect
 * is the minimal coupling.
 *
 * The pointer is read-only outside this module via {@link getActiveClientOtelHandle};
 * the provider is the sole writer via {@link setActiveClientOtelHandle}.
 */
let activeHandle: ClientOtelHandle | null = null;

export function setActiveClientOtelHandle(next: ClientOtelHandle | null): void {
  activeHandle = next;
}

export function getActiveClientOtelHandle(): ClientOtelHandle | null {
  return activeHandle;
}

/**
 * Default BFF SPA-span ingress path. Same-origin in deployments where
 * the BFF serves the SPA assets; for cross-origin (SPA at
 * `ai.smokeball.com`, BFF at `bff.smokeball.com`) the caller MUST pass
 * `ingestUrl` pointing at the BFF's `POST /spa-spans` route. The
 * `credentials: 'include'` mode ensures the cookie is attached on
 * both same-origin and cross-origin (the BFF's CORS plugin echoes
 * the SPA origin + sets `Access-Control-Allow-Credentials`).
 */
export const DEFAULT_SPA_SPANS_PATH = '/spa-spans' as const;

/**
 * Build the default sink (fn-1.31 round 7): POST each span to the BFF's
 * cookie-gated `/spa-spans` route. The BFF stamps the verified
 * identity, validates the payload, and forwards through the server-
 * side `LangfuseSpanEmitter` — so the SPA span lands on the SAME
 * Langfuse trace as the BFF/control-plane spans.
 *
 * **Why not direct-to-Langfuse**: Langfuse's public API requires
 * Basic Auth / API key auth, credentials we CANNOT safely embed in
 * the browser bundle. Routing through the BFF means cookie-based
 * auth + a single emitter on the server.
 *
 * **`keepalive: true`** lets the request survive page navigation —
 * important for click-then-redirect patterns (approve → navigate to
 * runs list).
 *
 * **`credentials: 'include'`** attaches the session cookie on both
 * same-origin and cross-origin posts. The BFF's CORS plugin handles
 * the credentialed preflight.
 *
 * **Failures swallowed**: observability MUST NOT block the click
 * handler. A 4xx/5xx from the BFF or a transport error drops the
 * span silently.
 *
 * @param opts.ingestUrl Optional absolute URL of the BFF span ingress.
 *   Defaults to {@link DEFAULT_SPA_SPANS_PATH} (same-origin relative).
 * @param opts.fetchFn Fetch override for tests.
 */
export function createBeaconSpanSink(opts?: {
  readonly ingestUrl?: string;
  readonly fetchFn?: typeof fetch;
}): ClientSpanSink {
  const ingestUrl = opts?.ingestUrl ?? readIngestUrlFromEnv() ?? DEFAULT_SPA_SPANS_PATH;
  const fetchFn = opts?.fetchFn ?? globalThis.fetch;
  return {
    emit(span: ClientSpan): void {
      if (typeof fetchFn !== 'function') return;
      try {
        // Fire-and-forget. `keepalive: true` lets the request survive
        // page navigation. Errors swallowed — observability MUST NOT
        // block the click handler.
        // codex round 10 — wire body uses `userSessionId` as the
        // BFF-visible `sessionId` (the route requires it to equal
        // the authenticated userId). The internal `span.sessionId`
        // (platform-session correlator when set) is sent as
        // `runSessionId`, but only when it differs from
        // `userSessionId` — otherwise it would be redundant.
        // We DO NOT send `traceId` — the BFF recomputes it
        // server-side from the authenticated userId. Keeps the
        // SPA's emit-to-Langfuse trace on `hash(userId)`; cross-tier
        // traceparent stitch is still anchored on `span.traceId`
        // via the outbound fetch wrapper.
        const isOverride = span.sessionId !== span.userSessionId && span.sessionId.length > 0;
        const body: Record<string, unknown> = {
          name: span.name,
          spanId: span.spanId,
          sessionId: span.userSessionId,
          startTime: span.startTime.toISOString(),
          endTime: span.endTime.toISOString(),
          metadata: span.metadata,
          level: span.level ?? 'DEFAULT',
        };
        if (isOverride) body.runSessionId = span.sessionId;
        // codex round 16 — beacon POST MUST carry `traceparent` so
        // the BFF's downstream ownership-verification call to the
        // control plane (`GET /sessions/:id`) nests under the SPA
        // span. Without this, normal session-coupled beacon traffic
        // produces a BFF→control-plane fetch with no `traceparent`,
        // violating R16's "every outbound HTTP call carries
        // traceparent" requirement. We stamp on `span.traceId` +
        // `span.spanId` so the verification call lands on the same
        // trace as the SPA span.
        void fetchFn(ingestUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            traceparent: `${TRACEPARENT_VERSION}-${span.traceId}-${span.spanId}-${FLAGS_SAMPLED}`,
            // fn-45.4 — the BFF CSRF preHandler requires this custom header
            // on unsafe methods (the /spa-spans POST is one).
            'X-Requested-With': 'fetch',
          },
          credentials: 'include',
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {
          // observe-only
        });
      } catch {
        // observe-only
      }
    },
  };
}

/**
 * Read the Vite-side BFF span ingress URL from `import.meta.env`.
 * Returns `undefined` when unset, in which case `createBeaconSpanSink`
 * falls back to {@link DEFAULT_SPA_SPANS_PATH} (same-origin relative).
 *
 * **What this URL is (fn-1.31 round 7)**: an absolute URL of the
 * BFF's `POST /spa-spans` endpoint — NOT a direct-to-Langfuse URL.
 * The BFF authenticates the user via cookie and forwards spans to
 * the server-side `LangfuseSpanEmitter`. We do NOT put Langfuse
 * credentials in the browser bundle.
 *
 * **Env vars consulted, in order**:
 *   1. `VITE_BFF_SPA_SPANS_URL` (preferred name, fn-1.31 round 7).
 *      Production wires the per-env BFF URL via the Vite build
 *      pipeline, e.g. `https://bff.dev.smokeball.com/spa-spans`.
 *   2. `VITE_LANGFUSE_INGEST_URL` (legacy name, kept for
 *      build-pipeline backward-compat during the round-7 rollout).
 *      MUST also point at the BFF `/spa-spans` route, NOT Langfuse
 *      directly — a misconfigured env var pointing at Langfuse will
 *      drop every span (no auth credentials available client-side).
 */
export function readIngestUrlFromEnv(): string | undefined {
  // The `import.meta.env` access path is Vite-specific; we narrow
  // defensively so node + test environments don't crash.
  const meta = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
  if (meta === undefined) return undefined;
  const next = meta.VITE_BFF_SPA_SPANS_URL;
  if (typeof next === 'string' && next.length > 0) return next;
  const legacy = meta.VITE_LANGFUSE_INGEST_URL;
  if (typeof legacy === 'string' && legacy.length > 0) return legacy;
  return undefined;
}

/**
 * Compute the deterministic trace id from a session id, using Web
 * Crypto's `subtle.digest('SHA-256', ...)`. Matches the platform's
 * `deterministicTraceIdFromSession` algorithm exactly:
 * `sha256(sessionId).slice(0, 32)`.
 *
 * Returns `null` when Web Crypto is unavailable (`globalThis.crypto`
 * absent on very old browsers or in a non-secure context).
 *
 * Exported for tests.
 */
export async function computeTraceIdFromSession(
  sessionId: string,
  crypto: Pick<Crypto, 'subtle'> | undefined = globalThis.crypto,
): Promise<string | null> {
  if (typeof sessionId !== 'string' || sessionId.length === 0) return null;
  if (crypto === undefined || typeof crypto.subtle?.digest !== 'function') return null;
  try {
    const data = new TextEncoder().encode(sessionId);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return bytesToHex(new Uint8Array(digest)).slice(0, TRACE_ID_HEX_LENGTH);
  } catch {
    return null;
  }
}

/**
 * Mint a fresh `parent-id` for an outbound `traceparent`. Random — not
 * cryptographically meaningful, just a correlator. 16-hex chars.
 */
function mintParentSpanId(crypto: Pick<Crypto, 'getRandomValues'> = globalThis.crypto): string {
  const bytes = new Uint8Array(PARENT_ID_HEX_LENGTH / 2);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

/** Convert a `Uint8Array` to a lowercase hex string. */
function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

/**
 * Build a `Headers` object pre-populated with `traceparent` keyed on
 * the SPA's session id. Convenience for callers building a one-off
 * fetch outside the `wrapFetch` path (e.g. an `EventSource`-adjacent
 * POST). Returns an empty `Headers` when the handle isn't ready.
 */
export function headersWithTraceparent(handle: ClientOtelHandle | null): Headers {
  const headers = new Headers();
  if (handle === null) return headers;
  const tp = handle.buildTraceparent();
  if (tp !== null) headers.set(TRACEPARENT_HEADER, tp);
  return headers;
}
